import { Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { NgxPermissionsService } from 'ngx-permissions';
import { BehaviorSubject } from 'rxjs';
import { pluck, withLatestFrom } from 'rxjs/operators';
import { Endpoint, UserRole } from '../entities/auth/endpoint.model';
import { AuthTokens } from '../entities/auth/tokens.model';
import { AuthUser } from '../entities/users/user';
import { loadAllPresets } from '../pages/viewer/store/color-presets-store/actions/ui-settings.actions';
import { extractPermissionNames, isAuthDataStored } from './auth.utils';
import { changeCurrentEndpointSuccess } from './store/auth/actions/auth.actions';
import {
  selectAuthData,
  selectCurrentEndpoint,
  selectTokens,
  selectUser,
} from './store/auth/selectors/auth.selectors';

/**
 * Holds the authenticated session state and keeps it in sync with the NgRx store.
 * AuthService extends this to expose the auth operations on top of the synced state.
 */
@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AuthStateService {
  protected user: AuthUser | null;
  protected endpoints: Endpoint[];
  protected tokens: AuthTokens | null;
  protected _currentEndpoint$ = new BehaviorSubject<Endpoint | null>(null);
  currentEndpoint$ = this._currentEndpoint$.asObservable();
  authChange = new BehaviorSubject<boolean>(false);
  endpointPath = new BehaviorSubject<string | null>(null);
  originalUrl: string | null = null;

  constructor(protected store: Store, protected permissionsService: NgxPermissionsService) {
    this.initStoreSubscriptions();
  }

  getCurrentEndpoint() {
    return this._currentEndpoint$.value;
  }

  setCurrentEndpoint(endpoint: Endpoint) {
    this.store.dispatch(changeCurrentEndpointSuccess({ endpoint }));
  }

  private initStoreSubscriptions() {
    this.store
      .select(selectTokens)
      .pipe(untilDestroyed(this))
      .subscribe((tokens) => {
        this.tokens = tokens;
      });
    this.store
      .select(selectCurrentEndpoint)
      .pipe(untilDestroyed(this))
      .subscribe((endpoint) => {
        this.endpointPath.next(endpoint?.endpoint.slice(0, -1) || null);
        this._currentEndpoint$.next(endpoint);
      });
    this.store
      .select(selectUser)
      .pipe(withLatestFrom(this.store.select(selectAuthData)))
      .subscribe(([_, data]) => {
        if (isAuthDataStored(data)) {
          [this.user, this.endpoints, this.tokens] = [data.user, data.endpoints, data.tokens];
          this.setCurrentEndpoint(data.currentEndpoint);
          this.authChange.next(true);
          this.store.dispatch(loadAllPresets());
        } else {
          this.user = null;
        }
      });
    this._currentEndpoint$
      .pipe(pluck('userRoles'), untilDestroyed(this))
      .subscribe((roles: UserRole[]) => {
        this.permissionsService.flushPermissions();
        if (roles) {
          this.permissionsService.loadPermissions(extractPermissionNames(roles));
        }
      });
  }
}
